import { z } from 'zod'

/**
 * Single feature flag schema for admin CRUD operations.
 * Used for creating, updating, and deleting individual flags.
 */
export const FeatureFlag = z.object({
  category: z.string().min(1, 'Kategorie ist erforderlich'),
  name: z.string().min(1, 'Name ist erforderlich'),
  enabled: z.boolean().default(false)
})

export type FeatureFlag = z.infer<typeof FeatureFlag>

/**
 * Helper to convert the nested FeatureFlags response to a flat array of FeatureFlag
 */
export function flattenFeatureFlags(
  nested: Record<string, Record<string, boolean>>
): FeatureFlag[] {
  const result: FeatureFlag[] = []

  for (const category of Object.keys(nested)) {
    const flags = nested[category]
    if (!flags) continue
    for (const name of Object.keys(flags)) {
      result.push({
        category,
        name,
        enabled: flags[name] ?? false
      })
    }
  }

  return result
}

/**
 * Get unique categories from a list of flags
 */
export function getCategories(flags: FeatureFlag[]): string[] {
  return [...new Set(flags.map((f) => f.category))].sort()
}

/**
 * Group flags by category
 */
export function groupByCategory(
  flags: FeatureFlag[]
): Record<string, FeatureFlag[]> {
  return flags.reduce(
    (acc, flag) => {
      const existing = acc[flag.category] ?? []
      acc[flag.category] = [...existing, flag]
      return acc
    },
    {} as Record<string, FeatureFlag[]>
  )
}
